import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { IconName } from 'components/Icon'

import { tx } from 'lib/i18n'

import { useActiveProject } from '../context/ActiveProjectContext'
import { useRegion } from '../context/RegionContext'
import { DailyReportsNav } from '../molecules/DailyReportsNav'
import { SidebarNavItem } from '../molecules/SidebarNavItem'
import { UserCard } from '../molecules/UserCard'

interface NavLeaf {
  icon: IconName
  label: string
}

const CALENDAR_NAV: NavLeaf = { icon: 'calendar', label: 'التقويم' }
const EXPORTS_NAV: NavLeaf = { icon: 'download', label: 'التصدير' }
const TEMPLATES_NAV: NavLeaf = { icon: 'file', label: 'القوالب' }

export const Sidebar = ({
  collapsed,
  drawerOpen = false,
  offCanvas = false,
  onDismiss,
}: {
  collapsed: boolean
  /** Mobile drawer is slid into view. */
  drawerOpen?: boolean
  /** Viewport is narrow enough that the sidebar is an off-canvas sheet. */
  offCanvas?: boolean
  onDismiss?: () => void
}) => {
  const { user } = useAuth()
  const { locale } = useRegion()
  const activeProject = useActiveProject()
  const location = useLocation()
  const projectsActive = location.pathname === '/' || location.pathname === '/projects/new'
  const isManagerAnywhere = Boolean(user?.isAdmin || user?.memberships.some((membership) => membership.role === 'PROJECT_MANAGER'))
  const [pendingApprovals, setPendingApprovals] = useState(0)
  const asideRef = useRef<HTMLElement>(null)
  const closedOffCanvas = offCanvas && !drawerOpen

  useEffect(() => {
    const node = asideRef.current
    if (!node) return
    if (closedOffCanvas) node.setAttribute('inert', '')
    else node.removeAttribute('inert')
  }, [closedOffCanvas])

  useEffect(() => {
    if (!isManagerAnywhere) return
    api
      .fetchApprovals('PENDING')
      .then((items) => setPendingApprovals(items.length))
      .catch(() => undefined)
  }, [isManagerAnywhere])

  return (
    <aside
      ref={asideRef}
      id="app-sidebar"
      className={['shell__sidebar', collapsed ? 'is-collapsed' : '', drawerOpen ? 'is-drawer-open' : ''].filter(Boolean).join(' ')}
      role={offCanvas && drawerOpen ? 'dialog' : undefined}
      aria-label={offCanvas && drawerOpen ? tx(locale, 'القائمة', 'Menu') : undefined}
      aria-hidden={closedOffCanvas || undefined}
      aria-modal={offCanvas && drawerOpen ? true : undefined}
      onClick={(event) => {
        if (!offCanvas || !drawerOpen) return
        const target = event.target
        if (target instanceof Element && target.closest('a')) onDismiss?.()
      }}
    >
      <Link to="/" className="shell__logo">
        <span className="brand__mark" aria-hidden="true">
          JP
        </span>
        <span className="shell__logo-text">JoPath</span>
      </Link>

      <nav className="shell__nav">
        <p className="shell__nav-heading">{tx(locale, 'الرئيسية', 'Home')}</p>
        <ul>
          <li>
            {isManagerAnywhere ? (
              <SidebarNavItem icon="grid" label={tx(locale, 'لوحة التحكم', 'Dashboard')} to="/dashboard" />
            ) : activeProject && activeProject.myRole === 'SITE_ENGINEER' ? (
              <SidebarNavItem icon="grid" label={tx(locale, 'اليوم', 'Today')} to={`/projects/${activeProject.id}/today`} />
            ) : (
              <SidebarNavItem icon="grid" label={tx(locale, 'لوحة التحكم', 'Dashboard')} disabled disabledHint={tx(locale, 'اختر مشروعاً أولاً', 'Choose a project first')} />
            )}
          </li>
          <li>
            <DailyReportsNav project={activeProject} />
          </li>
          <li>
            <SidebarNavItem
              icon="clipboardCheck"
              label={tx(locale, 'الاعتمادات', 'Approvals')}
              to={isManagerAnywhere ? '/approvals' : undefined}
              disabled={!isManagerAnywhere}
              disabledHint={tx(locale, 'لا تدير أي مشروع', 'You do not manage a project')}
              count={isManagerAnywhere && pendingApprovals ? pendingApprovals : undefined}
            />
          </li>
          <li>
            <SidebarNavItem icon="mapPin" label={tx(locale, 'المواقع', 'Sites')} to="/sites" />
          </li>
          <li>
            <SidebarNavItem icon={CALENDAR_NAV.icon} label={tx(locale, CALENDAR_NAV.label, 'Calendar')} to="/calendar" />
          </li>
        </ul>

        <p className="shell__nav-heading">{tx(locale, 'الموارد', 'Resources')}</p>
        <ul>
          <li>
            <SidebarNavItem
              icon="users"
              label={tx(locale, 'الكوادر', 'Crew')}
              to={user?.isAdmin ? '/crew' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
          <li>
            <SidebarNavItem
              icon="truck"
              label={tx(locale, 'المعدات', 'Equipment')}
              to={user?.isAdmin ? '/equipment' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
          <li>
            <SidebarNavItem
              icon="box"
              label={tx(locale, 'المواد', 'Materials')}
              to={user?.isAdmin ? '/materials' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
        </ul>

        <p className="shell__nav-heading">{tx(locale, 'الرؤى', 'Insights')}</p>
        <ul>
          <li>
            <SidebarNavItem
              icon="chart"
              label={tx(locale, 'التحليلات', 'Analytics')}
              to={user?.isAdmin ? '/analytics' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
          <li>
            <SidebarNavItem icon={EXPORTS_NAV.icon} label={tx(locale, EXPORTS_NAV.label, 'Exports')} to="/exports" />
          </li>
        </ul>

        <p className="shell__nav-heading">{tx(locale, 'الإدارة', 'Administration')}</p>
        <ul>
          <li>
            <SidebarNavItem icon="folder" label={tx(locale, 'المشاريع', 'Projects')} to="/" end active={projectsActive} />
          </li>
          <li>
            <SidebarNavItem
              icon="users"
              label={tx(locale, 'المستخدمون والأدوار', 'Users and roles')}
              to={user?.isAdmin ? '/admin/users' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
          <li>
            <SidebarNavItem icon={TEMPLATES_NAV.icon} label={tx(locale, TEMPLATES_NAV.label, 'Templates')} soon />
          </li>
          <li>
            <SidebarNavItem
              icon="settings"
              label={tx(locale, 'الإعدادات', 'Settings')}
              to={user?.isAdmin ? '/admin/settings/general' : undefined}
              disabled={!user?.isAdmin}
              disabledHint={tx(locale, 'لمدير النظام فقط', 'Admins only')}
            />
          </li>
        </ul>
      </nav>

      <UserCard user={user} />
    </aside>
  )
}
